'use client'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isPast,
} from 'date-fns'
import useAuctionManager from '@hooks/useAuctionManager'
import { useAsync } from 'react-async-hook'
import ClockIcon from '@assets/clock.svg'
const CountdownToEndOfDay = () => {
  const [timeLeft, setTimeLeft] = useState<null | {
    hours: number
    minutes: number
    seconds: number
    extraSeconds: number
  }>(null)

  const { currentListing, bids, auctionProgram } = useAuctionManager()

  const [realCurrentListing, setRealCurrentListing] = useState(currentListing)

  useAsync(async () => {
    const newCurrentListing = await auctionProgram.account.listingV0.fetch(
      currentListing.publicKey,
    )
    setRealCurrentListing((c) => ({
      ...newCurrentListing,
      publicKey: c.publicKey,
    }))
  }, [auctionProgram, bids])

  const setTime = useCallback(() => {
    const now = new Date()

    const auctionEndDate = new Date(realCurrentListing?.endAt.toNumber() * 1000)

    const hoursLeft = differenceInHours(auctionEndDate, now)
    const minutesLeft =
      differenceInMinutes(auctionEndDate, now) - hoursLeft * 60
    const secondsLeft =
      differenceInSeconds(auctionEndDate, now) -
      hoursLeft * 3600 -
      minutesLeft * 60

    // If the auction has ended refecth listings and set time to 0
    if (isPast(auctionEndDate)) {
      setTimeLeft({
        hours: 0,
        minutes: 0,
        seconds: 0,
        extraSeconds: 0,
      })
      return
    }

    setTimeLeft({
      hours: hoursLeft,
      minutes: minutesLeft,
      seconds: secondsLeft,
      extraSeconds: 0,
    })
  }, [realCurrentListing])

  const lessThan5Mins = useMemo(() => {
    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)
    return differenceInMinutes(auctionEndDate, new Date()) < 5
  }, [currentListing])

  useEffect(() => {
    setTime()
    const interval = setInterval(() => {
      setTime()
    }, 1000)

    return () => clearInterval(interval)
  }, [setTime])

  if (!timeLeft) {
    return <h2 className="w-32 h-10 rounded bg-dark-7 animate-pulse mt-2" />
  }

  return (
    <h5
      className={`flex items-center gap-2 text-size-4 font-semibold ${lessThan5Mins ? 'text-red-7' : ''}`}
    >
      {timeLeft.hours}H {timeLeft.minutes}M {timeLeft.seconds}S
      {lessThan5Mins && <ClockIcon color="var(--dewi_colors_red_7)" />}
    </h5>
  )
}

export default CountdownToEndOfDay
